import React, { useState } from 'react'
import { Stack, Typography, Badge, Button, Tooltip, IconButton } from '@mui/material'
import { Portal } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import PdsIcon from '@/components/ui/icons/Pds'
import DoctorIcon from '@/components/ui/icons/Doctor'
import MeetingClockComponent from '../components/MeetingClock'
import NetworkSpeed from './NetworkSpeed'
import Dictaphone from './Dictaphone'
import TopbarButton from '../../../layouts/diagnostic/TopbarButton'

import { useTranslation } from 'react-i18next'
import { useDiagnosticLayout } from '@/layouts/diagnostic/DiagnosticLayout'
import { Consultation, ConsultationStatus } from '@/core/models/consultation.model'
import colors from '@/assets/scss/_themes-vars.module.scss'
import { ChatMessage } from '@/core/models/chat-room.model'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import VideocamIcon from '@mui/icons-material/Videocam'
import { AuthUser } from '@/core/classes/Auth'

interface TopbarProps {
  authUser: AuthUser
  consultation: Consultation
  pds: string
  doctor: string
  isPdsOnline: boolean
  isDoctorOnline: boolean
  readOnly: boolean
  consultationId: string | undefined
  openParticipantsDialog: boolean
  setOpenParticipantsDialog: React.Dispatch<React.SetStateAction<boolean>>
  unreadChatMessages: ChatMessage[]
  toggleMessenger: () => void
  handleCloseConsultation: () => void
  isConsultationActive: boolean
  isInfo: boolean
  setIsInfo: React.Dispatch<React.SetStateAction<boolean>>
}

const MobileConsultationTopbar = ({
  authUser,
  consultation,
  pds,
  doctor,
  isPdsOnline,
  isDoctorOnline,
  readOnly,
  consultationId,
  openParticipantsDialog,
  setOpenParticipantsDialog,
  unreadChatMessages,
  toggleMessenger,
  handleCloseConsultation,
  isConsultationActive,
  isInfo,
  setIsInfo,
}: TopbarProps) => {
  const { topbarContentContainer } = useDiagnosticLayout()
  const { t } = useTranslation()

  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Portal container={topbarContentContainer}>
      <Stack direction="column" alignItems="center" sx={{ width: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', backgroundColor: 'primary.main', p: 1 }}>
          <Typography variant="h1" sx={{ color: 'white', fontSize: '14px', fontWeight: 500 }}>
            {t('Live')}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {!readOnly && <Dictaphone consultationId={consultationId} />}
            <Badge badgeContent={unreadChatMessages.length} color="error" max={9} overlap="rectangular">
              <TopbarButton id="chat-button" onClick={toggleMessenger} variant="contained">
                <ChatOutlinedIcon />
              </TopbarButton>
            </Badge>
            {!readOnly && (
              <TopbarButton id="invite-button" onClick={() => setOpenParticipantsDialog(!openParticipantsDialog)} variant="contained">
                <PersonAddAltIcon />
              </TopbarButton>
            )}
            <TopbarButton id="switch-mode" onClick={() => setIsInfo(!isInfo)} variant="contained">
              {isInfo ? <VideocamIcon /> : <InfoOutlinedIcon />}
            </TopbarButton>
            <IconButton onClick={() => setIsExpanded(!isExpanded)} sx={{ color: 'white' }}>
              {isExpanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </IconButton>
          </Stack>
        </Stack>
        {isExpanded && (
          <Stack direction="column" alignItems="center" sx={{ width: '100%', backgroundColor: 'primary.dark', p: 1 }} spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
              {authUser._id === consultation.medecin?._id ? (
                <>
                  <PdsIcon
                    isFemale={consultation.pds?.userInfo?.gender === 'F'}
                    htmlColor="#FFF"
                    height={'45px'}
                    width={'45px'}
                    sx={{
                      backgroundColor: 'white',
                      height: '45px',
                      width: '45px',
                      border: '3px solid',
                      borderColor: isPdsOnline ? colors.green400 : colors.red500,
                      borderRadius: '40px',
                    }}
                  />
                  <Stack direction="column">
                    <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
                      {`${pds}`}
                    </Typography>
                    <Typography fontSize="10px" fontWeight="300" sx={{ color: (theme) => theme.palette.common.white }}>
                      {isPdsOnline ? t('common:online') : t('common:offline')}
                    </Typography>
                  </Stack>
                </>
              ) : (
                <>
                  <DoctorIcon
                    isFemale={consultation.medecin?.userInfo?.gender === 'F'}
                    htmlColor="#FFF"
                    height={'45px'}
                    width={'45px'}
                    sx={{
                      backgroundColor: 'white',
                      height: '45px',
                      width: '45px',
                      border: '3px solid',
                      borderColor: isDoctorOnline ? colors.green400 : colors.red500,
                      borderRadius: '40px',
                    }}
                  />
                  <Stack direction="column">
                    <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
                      Dr. {`${doctor}`}
                    </Typography>
                    <Typography fontSize="10px" fontWeight="300" sx={{ color: (theme) => theme.palette.common.white }}>
                      {isDoctorOnline ? t('common:online') : t('common:offline')}
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>
            <Stack direction="row" spacing={4} sx={{ mt: 1 }}>
              <MeetingClockComponent startDate={consultation.startDate} consultationStatus={ConsultationStatus.Active} />
              <NetworkSpeed />
            </Stack>
            <Button color="error" onClick={handleCloseConsultation} variant="contained">
              {t(`page.liveConsultation.topbar.${isConsultationActive ? 'endConsultationButton' : 'quitConsultationButton'}.label`)}
            </Button>
          </Stack>
        )}
      </Stack>
    </Portal>
  )
}

export default MobileConsultationTopbar
